import { type CSSProperties, useMemo, useState } from 'react'
import Plot from 'react-plotly.js'
import { Button, Checkbox, Dropdown, Header, Message, Segment } from 'semantic-ui-react'
import {
  type Channel,
  type RfiMeasurement,
  SFU_SCALE,
  type SkyScan,
  type ValueScale,
  elevationRange,
  formatMeasurementPeriod,
  measurementPeriod,
  toSkyScan,
} from './rfiScan'
import {
  type DomeMode,
  type Pointing,
  type SkySamples,
  buildDomeTraces,
  buildPanoramaTelescopeTraces,
  buildPanoramaTraces,
  buildPolarTelescopeTraces,
  buildPolarTraces,
  domeLayout,
  isOnPanorama,
  lazySamples,
  once,
  panoramaLayout,
  polarLayout,
} from './skyFigures'
import { type SkyPoint, clipElevation, idw } from './skyInterpolation'
import { useAfterPaint, useHeldWhileDragging, useThrottled } from './steadyValue'
import { useRfiMeasurements } from '../../hooks/useRfiMeasurements'
import { useTelescopePosition } from '../../hooks/useTelescopePosition'

type SkyView = 'panorama' | 'ground' | 'dome'

const IDW_RADIUS_DEGREES = 10
// The live position is rounded to this many decimals: finer changes (encoder noise of a parked
// telescope) would redraw the whole chart for nothing.
const DEGREE_DECIMAL_PLACES = 1
const POINTING_INTERVAL_MS = 2000
// Half a scan step: values are drawn up to the edge of the outermost measured rows, no further.
const BAND_MARGIN_DEGREES = 0.5

const pageStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '1.5rem' }

const controlsStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.8rem 1.5rem',
  alignItems: 'flex-end',
}

const fieldStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '0.3rem' }

const fieldLabelStyle: CSSProperties = { color: '#666', fontSize: '0.9rem' }

const chartSegmentStyle: CSSProperties = { margin: 0, minWidth: 0 }

const chartStyle: CSSProperties = { width: '100%', height: '560px' }

const pendingChartStyle: CSSProperties = { opacity: 0.4 }

const pendingHintStyle: CSSProperties = { color: '#666', fontSize: '0.85rem', margin: '0 0 0.5rem' }

const panoramaChartStyle: CSSProperties = { width: '100%', height: '360px' }

const periodStyle: CSSProperties = { color: '#666', textAlign: 'center', margin: '-1rem 0 0' }

const chartCaptionStyle: CSSProperties = { color: '#666', fontSize: '0.85rem', margin: '0 0 0.5rem' }

const plotConfig = { responsive: true, displaylogo: false }

// The ground view is a fixed sky chart: selecting regions of it means nothing.
const groundPlotConfig = { ...plotConfig, modeBarButtonsToRemove: ['select2d', 'lasso2d'] }

const domePlotConfig = { ...plotConfig, modeBarButtonsToRemove: ['resetCameraLastSave3d'] }

const viewOptions: Array<{ value: SkyView; text: string }> = [
  { value: 'panorama', text: 'Panorama' },
  { value: 'ground', text: 'Sky view from the ground' },
  { value: 'dome', text: '3D dome' },
]

const channelOptions = [
  { key: 'f10', value: 'f10', text: 'F10' },
  { key: 'f30', value: 'f30', text: 'F30' },
]

const domeOptions = [
  { key: 'sphere', value: 'sphere', text: 'Sphere' },
  { key: 'relief', value: 'relief', text: 'Relief (height = value)' },
]

const formatDegrees = (value: number) => `${value.toFixed(DEGREE_DECIMAL_PLACES)}°`

const roundDegrees = (value: number) => Number(value.toFixed(DEGREE_DECIMAL_PLACES))

type LivePosition = { az: number; el: number; target?: string }

// Everything drawn for one channel. Built on first use and kept, so switching back to a channel is
// instant.
function prepareChannel(measurements: RfiMeasurement[], channel: Channel) {
  const scan = toSkyScan(measurements, channel, 'both')
  const band = elevationRange(scan)
  const [vmin, vmax] = valueRange(scan)
  const period = measurementPeriod(scan)
  if (!band) return { scan, band, vmin, vmax, period, interpolator: null, samples: null }
  const base = idw(scan.points, { radius: IDW_RADIUS_DEGREES })
  const interpolator = clipElevation(base, band[0] - BAND_MARGIN_DEGREES, band[1] + BAND_MARGIN_DEGREES)
  return { scan, band, vmin, vmax, period, interpolator, samples: lazySamples(interpolator, band) }
}

function valueRange(scan: SkyScan): [number, number] {
  const values = scan.points.map((p) => p.value).filter(Number.isFinite)
  if (!values.length) return [0, 1]
  return [Math.min(...values), Math.max(...values)]
}

type ChartProps = {
  points: SkyPoint[]
  band: [number, number]
  samples: SkySamples
  vmin: number
  vmax: number
  scale: ValueScale
  showPoints: boolean
  // Absent until the telescope reports its position.
  pointing?: Pointing
  onRelayouting: () => void
}

// Each view samples the interpolation on its own grid, so only the selected one is mounted.
// The measured traces are built apart from the telescope ones, so a new pointing reuses them.
function PanoramaChart(props: ChartProps) {
  const { points, band, samples, vmin, vmax, scale, showPoints, pointing, onRelayouting } = props
  const panorama = useMemo(() => samples.panorama(), [samples])
  const traces = useMemo(
    () => buildPanoramaTraces({ panorama, points, showPoints, vmin, vmax, scale }),
    [panorama, points, showPoints, vmin, vmax, scale],
  )
  const data = useMemo(
    () => (pointing && isOnPanorama(pointing.el, band)
      ? [...traces, ...buildPanoramaTelescopeTraces(pointing)]
      : traces),
    [traces, pointing, band],
  )
  const layout = useMemo(() => panoramaLayout(band), [band])

  return (
    <>
      <p style={chartCaptionStyle}>
        The measured band above the horizon, stretched over the full height. Azimuth from north through east.
      </p>
      <Plot
        data={data}
        layout={layout}
        config={plotConfig}
        style={panoramaChartStyle}
        onRelayouting={onRelayouting}
        useResizeHandler
      />
    </>
  )
}

function GroundChart({ points, samples, vmin, vmax, scale, showPoints, pointing, onRelayouting }: ChartProps) {
  const values = useMemo(() => samples.polar(), [samples])
  const traces = useMemo(
    () => buildPolarTraces({ values, points, showPoints, vmin, vmax, scale }),
    [values, points, showPoints, vmin, vmax, scale],
  )
  const data = useMemo(
    () => (pointing ? [...traces, ...buildPolarTelescopeTraces(pointing)] : traces),
    [traces, pointing],
  )

  return (
    <>
      <p style={chartCaptionStyle}>
        Zenith in the centre, horizon at the edge. Like a sky chart: north up, east on the left.
      </p>
      <Plot
        data={data}
        layout={polarLayout}
        config={groundPlotConfig}
        style={chartStyle}
        onRelayouting={onRelayouting}
        useResizeHandler
      />
    </>
  )
}

function DomeChart({
  points,
  samples,
  vmin,
  vmax,
  scale,
  showPoints,
  onRelayouting,
  domeMode,
}: ChartProps & { domeMode: DomeMode }) {
  const values = useMemo(() => samples.dome(), [samples])
  // No telescope pointing on the dome: the scan is the only content.
  const data = useMemo(
    () => buildDomeTraces({ values, points, showPoints, mode: domeMode, vmin, vmax, scale }),
    [values, points, showPoints, domeMode, vmin, vmax, scale],
  )

  return (
    <>
      <p style={chartCaptionStyle}>
        Seen from outside: from above, east is on the right. Drag to rotate, scroll to zoom.
      </p>
      <Plot
        data={data}
        layout={domeLayout}
        config={domePlotConfig}
        style={chartStyle}
        onRelayouting={onRelayouting}
        useResizeHandler
      />
    </>
  )
}

function InterferenceMap() {
  const { measurements, isLoading, error } = useRfiMeasurements()
  const { position } = useTelescopePosition()
  const [channel, setChannel] = useState<Channel>('f10')
  const [view, setView] = useState<SkyView>('panorama')
  const [domeMode, setDomeMode] = useState<DomeMode>('relief')
  const [showPoints, setShowPoints] = useState(false)
  // The chart follows the controls one painted frame later: a new view, channel or set of points can
  // block the page for a moment, and the controls should show the new choice before that, not after.
  const chartView = useAfterPaint(view)
  const chartChannel = useAfterPaint(channel)
  const chartShowPoints = useAfterPaint(showPoints)
  const isPreparing = chartView !== view || chartChannel !== channel || chartShowPoints !== showPoints

  const scale = SFU_SCALE
  const channels = useMemo(
    () => ({ f10: once(() => prepareChannel(measurements, 'f10')), f30: once(() => prepareChannel(measurements, 'f30')) }),
    [measurements],
  )
  const { scan, band, vmin, vmax, period, interpolator, samples } = channels[chartChannel]()

  // Every websocket message is a new object, even for a parked telescope; each new pointing redraws
  // the whole chart, so it changes only when the rounded position does, at most once per interval,
  // and never while the user drags the chart.
  const az = position ? roundDegrees(position.azimuthDegrees) : undefined
  const el = position ? roundDegrees(position.elevationDegrees) : undefined
  const target = position?.targetName
  const livePosition: LivePosition | undefined = useMemo(
    () => (az === undefined || el === undefined ? undefined : { az, el, target }),
    [az, el, target],
  )
  const throttledPosition = useThrottled(livePosition, POINTING_INTERVAL_MS)
  const { value: shownPosition, onRelayouting } = useHeldWhileDragging(throttledPosition)

  const pointing: Pointing | undefined = useMemo(() => {
    if (!shownPosition) return undefined
    const value = interpolator ? interpolator(shownPosition.az, shownPosition.el) : null
    const targetLabel = shownPosition.target ? ` → ${shownPosition.target}` : ''
    const reading = value === null ? 'no data' : scale.format(value)
    return {
      az: shownPosition.az,
      el: Math.max(shownPosition.el, 0), // below the horizon: draw on the horizon
      hover: `<b>RT6m${targetLabel}</b><br>az ${formatDegrees(shownPosition.az)} · el ${formatDegrees(shownPosition.el)}<br>${reading}`,
    }
  }, [shownPosition, interpolator, scale])

  const hasScan = !isLoading && !error

  const chartProps = band && samples
    ? { points: scan.points, band, samples, vmin, vmax, scale, showPoints: chartShowPoints, pointing, onRelayouting }
    : null

  return (
    <div className='fade-in' style={pageStyle}>
      <Header as='h1' style={{ textAlign: 'center' }}>
        Map of radio interference
      </Header>
      {hasScan && period && <p style={periodStyle}>{formatMeasurementPeriod(period)}</p>}

      {isLoading && <Message info>Loading interference measurements...</Message>}
      {error && <Message warning>Interference measurements are currently unavailable.</Message>}

      {hasScan && (
        <Segment>
          <div style={controlsStyle}>
            <div style={fieldStyle}>
              <span style={fieldLabelStyle}>View</span>
              <Button.Group size='small' aria-label='View'>
                {viewOptions.map((option) => (
                  <Button
                    key={option.value}
                    type='button'
                    active={view === option.value}
                    onClick={() => setView(option.value)}
                  >
                    {option.text}
                  </Button>
                ))}
              </Button.Group>
            </div>

            <div style={fieldStyle}>
              <label style={fieldLabelStyle} htmlFor='sky-channel'>Channel</label>
              <Dropdown
                id='sky-channel'
                selection
                options={channelOptions}
                value={channel}
                onChange={(_e, { value }) => setChannel(value as Channel)}
              />
            </div>

            {view === 'dome' && (
              <div style={fieldStyle}>
                <label style={fieldLabelStyle} htmlFor='sky-dome'>Dome</label>
                <Dropdown
                  id='sky-dome'
                  selection
                  options={domeOptions}
                  value={domeMode}
                  onChange={(_e, { value }) => setDomeMode(value as DomeMode)}
                />
              </div>
            )}

            <Checkbox
              toggle
              label='Measurement points'
              checked={showPoints}
              onChange={(_e, { checked }) => setShowPoints(Boolean(checked))}
            />
          </div>
        </Segment>
      )}

      {hasScan && (chartProps ? (
        <Segment style={chartSegmentStyle}>
          <Header as='h3'>{viewOptions.find((option) => option.value === chartView)?.text}</Header>
          {isPreparing && <p style={pendingHintStyle}>Preparing the view…</p>}
          <div style={isPreparing ? pendingChartStyle : undefined}>
            {chartView === 'panorama' && <PanoramaChart {...chartProps} />}
            {chartView === 'ground' && <GroundChart {...chartProps} />}
            {chartView === 'dome' && <DomeChart {...chartProps} domeMode={domeMode} />}
          </div>
        </Segment>
      ) : (
        <Message>No measurements for the selected channel.</Message>
      ))}
    </div>
  )
}

export default InterferenceMap
